import { useCallback, useEffect, useState } from "react";
import { HttpError, request } from "../../../api/http";
import { IconClose } from "../../../components/Icons";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";

type Props = {
  subscriptionId: string;
  leadUlid: string | null;
  currentDefault: string;
  onClose: () => void;
  onChanged: () => void;
};

// Shape returned by DomainPaymentMethodDto on the backend. Keep the field
// names verbatim instead of normalising to Stripe-style aliases (last4,
// expiration, …) so the contract stays aligned with the legacy admin panel
// which already consumes these exact keys.
type PaymentMethod = {
  paymentMethodId: string;
  customerId: string;
  type: string;
  lastFourDigits: string;
  expiredDate: string;
};

type Tab = "list" | "add-sepa";

type BillingDetails = {
  email?: string;
  name?: string;
  phone?: string;
  [k: string]: unknown;
};

export default function PaymentMethodsDrawer({
  subscriptionId,
  leadUlid,
  currentDefault,
  onClose,
  onChanged,
}: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [tab, setTab] = useState<Tab>("list");
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const { width, startResize } = useResizableDrawer({ initial: 640, min: 420 });

  const loadMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<{ methods: PaymentMethod[] }>(
        `/api/admin/operations/subscriptions/${subscriptionId}/payment-methods`,
        { token },
      );
      setMethods(data.methods);
    } catch (err) {
      setError(
        err instanceof HttpError ? `HTTP ${err.status}` : "No se pudieron cargar",
      );
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, token]);

  useEffect(() => {
    loadMethods();
  }, [loadMethods]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const setAsDefault = async (method: PaymentMethod) => {
    const paymentMethodId = method.paymentMethodId;
    const type = method.type;
    if (!paymentMethodId || !type) {
      setError("Método de pago sin id o type");
      return;
    }
    const ok = await confirm({
      title: "Cambiar método de pago default",
      message: `Marcar este método (${type}${method.lastFourDigits ? ` ·${method.lastFourDigits}` : ""}) como default?`,
      confirmLabel: "Marcar como default",
    });
    if (!ok) return;
    setUpdatingId(paymentMethodId);
    setError(null);
    try {
      await request(
        `/api/admin/operations/subscriptions/${subscriptionId}/payment-methods/default`,
        {
          method: "POST",
          token,
          // The endpoint's payload field is called paymentExternalId for
          // historical reasons, but the value it expects is the Stripe
          // payment method id (pm_…) — same value DomainPaymentMethodDto
          // surfaces as paymentMethodId.
          body: { paymentExternalId: paymentMethodId, type },
        },
      );
      await loadMethods();
      onChanged();
    } catch (err) {
      setError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Cambio falló",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          height: "100%",
          background: "#f8f9fb",
          boxShadow: "-2px 0 16px rgba(0,0,0,.18)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "left",
        }}
      >
        <ResizeHandle onMouseDown={startResize} />
        <header
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.85rem 1.1rem",
            background: "white",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: "1rem", color: "#1f2933" }}>
              Métodos de pago
            </h2>
            <div style={{ fontSize: ".75rem", color: "#73879C" }}>
              Default actual: <code>{currentDefault || "—"}</code>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar"
            style={closeBtnStyle}
          >
            <IconClose />
          </button>
        </header>

        <div
          role="tablist"
          style={{
            display: "flex",
            gap: "0.25rem",
            padding: "0.5rem 1.1rem 0",
            background: "white",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <TabBtn active={tab === "list"} onClick={() => setTab("list")}>
            Listado
          </TabBtn>
          <TabBtn
            active={tab === "add-sepa"}
            onClick={() => setTab("add-sepa")}
          >
            Añadir SEPA
          </TabBtn>
        </div>

        {error && (
          <div
            style={{
              padding: "0.6rem 1.1rem",
              background: "#fde2e2",
              color: "#b32424",
              fontSize: ".82rem",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: "auto", padding: "1rem 1.1rem" }}>
          {tab === "list" ? (
            <PaymentMethodsList
              methods={methods}
              loading={loading}
              currentDefault={currentDefault}
              updatingId={updatingId}
              onSetDefault={setAsDefault}
            />
          ) : (
            <AddSepaForm
              subscriptionId={subscriptionId}
              leadUlid={leadUlid}
              onAdded={() => {
                setTab("list");
                loadMethods();
                onChanged();
              }}
              onError={setError}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentMethodsList({
  methods,
  loading,
  currentDefault,
  updatingId,
  onSetDefault,
}: {
  methods: PaymentMethod[];
  loading: boolean;
  currentDefault: string;
  updatingId: string | null;
  onSetDefault: (m: PaymentMethod) => void;
}) {
  if (loading) {
    return <div style={{ color: "#73879C" }}>Cargando…</div>;
  }
  if (methods.length === 0) {
    return <div style={{ color: "#73879C" }}>Sin métodos de pago</div>;
  }
  return (
    <div style={{ display: "grid", gap: "0.6rem" }}>
      {methods.map((m, i) => {
        const paymentMethodId = m.paymentMethodId;
        const isDefault = paymentMethodId === currentDefault;
        const submitting = updatingId === paymentMethodId;
        return (
          <div
            key={paymentMethodId || i}
            style={{
              background: "white",
              borderRadius: "0.5rem",
              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
              padding: "0.7rem 0.95rem",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: ".85rem", color: "#1f2933" }}>
                <strong>{m.type || "—"}</strong>
                {m.lastFourDigits && <span> · ····{m.lastFourDigits}</span>}
                {m.expiredDate && <span> · exp {m.expiredDate}</span>}
              </div>
              <div
                style={{
                  fontSize: ".72rem",
                  color: "#73879C",
                  fontFamily: "monospace",
                  marginTop: 2,
                }}
              >
                {paymentMethodId || "—"}
              </div>
            </div>
            {isDefault ? (
              <span
                style={{
                  fontSize: ".7rem",
                  fontWeight: 700,
                  padding: "0.15rem 0.5rem",
                  background: "#d4edda",
                  color: "#155724",
                  borderRadius: "999px",
                  letterSpacing: ".4px",
                  textTransform: "uppercase",
                }}
              >
                Default
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onSetDefault(m)}
                disabled={submitting}
                style={smallBtn}
              >
                {submitting ? "…" : "Marcar default"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AddSepaForm({
  subscriptionId,
  leadUlid,
  onAdded,
  onError,
}: {
  subscriptionId: string;
  leadUlid: string | null;
  onAdded: () => void;
  onError: (msg: string) => void;
}) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [iban, setIban] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [prefilling, setPrefilling] = useState(false);

  useEffect(() => {
    if (!leadUlid) return;
    setPrefilling(true);
    request<BillingDetails>(
      `/api/admin/operations/leads/${leadUlid}/billing-details`,
      { token },
    )
      .then((data) => {
        setName(data.name ?? "");
        setEmail(data.email ?? "");
        setPhone(data.phone ?? "");
      })
      .catch(() => {
        // Soft-fail: just leave the form empty so ops can type the data.
      })
      .finally(() => setPrefilling(false));
  }, [leadUlid, token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!iban.trim()) {
      onError("Falta IBAN");
      return;
    }
    const ok = await confirm({
      title: "Añadir método SEPA",
      message:
        "¿Añadir este método SEPA a la suscripción? Stripe creará una nueva fuente de pago.",
      confirmLabel: "Añadir SEPA",
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await request(
        `/api/admin/operations/subscriptions/${subscriptionId}/payment-methods`,
        {
          method: "POST",
          token,
          body: {
            paymentMethod: iban.trim(),
            billingDetails: {
              typeOfPaymentMethod: "SEPA",
              billingDetails: {
                name,
                email,
                phone,
              },
            },
          },
        },
      );
      setIban("");
      onAdded();
    } catch (err) {
      onError(
        err instanceof HttpError
          ? `HTTP ${err.status}: ${((err.body as { message?: string })?.message ?? "").slice(0, 200)}`
          : "Añadir SEPA falló",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        background: "white",
        borderRadius: "0.55rem",
        padding: "0.95rem 1.1rem",
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        display: "grid",
        gap: "0.65rem",
      }}
    >
      <FormField label="IBAN">
        <input
          type="text"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="ESxx ..."
          style={inputStyle}
          required
        />
      </FormField>
      <FormField label="Nombre">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={prefilling ? "Cargando…" : "Nombre"}
          style={inputStyle}
        />
      </FormField>
      <FormField label="Email">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={prefilling ? "Cargando…" : "email@dominio.com"}
          style={inputStyle}
        />
      </FormField>
      <FormField label="Teléfono">
        <input
          type="text"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={prefilling ? "Cargando…" : "+34..."}
          style={inputStyle}
        />
      </FormField>
      <button
        type="submit"
        disabled={submitting || prefilling}
        style={{
          padding: "0.5rem 1.1rem",
          borderRadius: "0.4rem",
          background: "#0069d9",
          color: "white",
          border: "1px solid #0069d9",
          fontSize: ".82rem",
          fontWeight: 600,
          cursor: submitting ? "not-allowed" : "pointer",
          opacity: submitting ? 0.6 : 1,
          marginTop: "0.3rem",
          justifySelf: "end",
        }}
      >
        {submitting ? "Añadiendo…" : "Añadir SEPA"}
      </button>
    </form>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: "0.25rem" }}>
      <span style={fieldLabelStyle}>{label}</span>
      {children}
    </label>
  );
}

function TabBtn({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: "0.5rem 1rem",
        background: "transparent",
        color: active ? "#0069d9" : "#73879C",
        border: "none",
        borderBottom: `2px solid ${active ? "#0069d9" : "transparent"}`,
        fontSize: ".82rem",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "0.4rem 0.55rem",
  borderRadius: "0.4rem",
  border: "1px solid #dee2e6",
  background: "#f8f9fb",
  fontSize: ".88rem",
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: ".72rem",
  color: "#73879C",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: ".3px",
};

const closeBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "2rem",
  height: "2rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  cursor: "pointer",
};

const smallBtn: React.CSSProperties = {
  padding: "0.3rem 0.7rem",
  borderRadius: "0.35rem",
  background: "white",
  border: "1px solid #dee2e6",
  color: "#1f2933",
  fontSize: ".75rem",
  fontWeight: 600,
  cursor: "pointer",
};
